import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Star, MapPin, ArrowLeft, Loader2, ShieldCheck, ChevronRight, Calendar, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { fetchUniversity, fetchAuthor } from '../lib/api';
import Seo from '../components/Seo';
import { buildUniversitySchema } from '../lib/schema';
import NotFound from './NotFound';

const UniversityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(undefined);
  const [author, setAuthor] = useState(null);

  useEffect(() => {
    setState(undefined); setAuthor(null);
    fetchUniversity(id)
      .then((res) => {
        setState(res);
        const src = res.kind === 'parent' ? res.parent : res.data;
        if (src?.author_id) fetchAuthor(src.author_id).then(setAuthor).catch(() => {});
      })
      .catch(() => setState(null));
  }, [id]);

  if (state === undefined) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;
  if (state === null) return <NotFound />;

  const { kind, data, parent, subpages } = state;
  const isSub = kind === 'sub';
  const uni = parent;
  const activeSlug = isSub ? data.slug : parent.slug;
  const seoSrc = data;

  return (
    <div className="bg-slate-50 min-h-screen">
      <Seo
        title={seoSrc.meta_title || `${isSub ? data.tab_label + ' - ' : ''}${uni.name} | Learndunia`}
        description={seoSrc.meta_description || `${uni.name} - ${uni.type} University established in ${uni.established}.`}
        keywords={seoSrc.meta_keywords}
        image={seoSrc.og_image || uni.image}
        canonical={seoSrc.canonical_url || `https://learndunia.com/universities/${activeSlug}`}
        jsonLd={buildUniversitySchema(uni)}
      />

      <div className="relative h-56 md:h-72 overflow-hidden">
        <img src={uni.image} alt={uni.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent" />
        <div className="absolute inset-x-0 top-4 max-w-7xl mx-auto px-4">
          <button onClick={() => navigate('/universities')} className="inline-flex items-center gap-1 text-white/90 hover:text-white text-sm bg-black/30 backdrop-blur px-3 py-1.5 rounded-full">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
        <div className="absolute bottom-6 left-0 right-0 max-w-7xl mx-auto px-4 text-white">
          <div className="flex items-center gap-2 text-xs text-white/70 mb-2">
            <Link to="/universities" className="hover:text-white">Universities</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/universities/${uni.slug}`} className="hover:text-white">{uni.name}</Link>
            {isSub && (<><ChevronRight className="w-3 h-3" /><span>{data.tab_label}</span></>)}
          </div>
          <Badge className="bg-orange-500 hover:bg-orange-500 mb-2">{uni.type}</Badge>
          <h1 className="text-3xl md:text-5xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {isSub ? `${uni.name}: ${data.tab_label}` : uni.name}
          </h1>
          <div className="flex flex-wrap gap-3 mt-2 text-sm text-white/80">
            <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" /> {uni.location}</span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" /> Est. {uni.established}</span>
            <span className="inline-flex items-center gap-1"><Award className="w-4 h-4" /> NAAC {uni.naac}</span>
            <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {uni.rating}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6 bg-white border border-slate-200 rounded-xl p-1 overflow-x-auto flex gap-1">
          <Link to={`/universities/${uni.slug}`}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${!isSub ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
            Overview
          </Link>
          {subpages.map((sp) => (
            <Link key={sp.id} to={`/universities/${sp.slug}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${isSub && sp.slug === activeSlug ? 'bg-teal-600 text-white' : 'text-slate-700 hover:bg-slate-100'}`}>
              {sp.tab_label}
            </Link>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
            {isSub ? (
              <>
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{data.tab_label}</h2>
                {data.content ? <div className="prose prose-slate max-w-none mt-4" dangerouslySetInnerHTML={{ __html: data.content }} /> : <p className="text-slate-500 mt-3">Content coming soon.</p>}
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>About {uni.name}</h2>
                {uni.content ? (
                  <div className="prose prose-slate max-w-none mt-4" dangerouslySetInnerHTML={{ __html: uni.content }} />
                ) : (
                  <p className="mt-3 text-slate-700 leading-relaxed">{uni.name} is a {uni.type.toLowerCase()} university located in {uni.location}, established in {uni.established}. It is accredited NAAC {uni.naac} and offers {uni.courses}+ courses across multiple disciplines.</p>
                )}
              </>
            )}
            {author && (
              <div className="mt-8 pt-6 border-t border-slate-100 flex items-center gap-3">
                <img src={author.avatar} alt={author.name} className="w-12 h-12 rounded-full object-cover" />
                <div>
                  <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                    Reviewed by {author.is_fact_checker && <ShieldCheck className="w-3 h-3 text-teal-600" />}
                  </div>
                  <Link to={`/author/${author.slug}`} className="font-semibold text-slate-900 hover:text-teal-700">{author.name}</Link>
                  <div className="text-xs text-slate-500">{author.designation}</div>
                </div>
              </div>
            )}
          </div>
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white rounded-2xl p-6 h-fit">
            <h3 className="font-bold text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Talk to an Expert</h3>
            <p className="text-teal-50 text-sm mt-1">Get personalised guidance about {uni.name}.</p>
            <div className="mt-4 space-y-2">
              <input placeholder="Your Name" className="w-full px-3 py-2.5 rounded-lg text-slate-900 text-sm outline-none" />
              <input placeholder="Mobile Number" className="w-full px-3 py-2.5 rounded-lg text-slate-900 text-sm outline-none" />
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white">Request Callback</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversityDetail;
